import React, { useEffect, useState } from 'react'
import {
  type PersonAddress,
  PersonBackgroundInfoBlock,
  PersonBasicInfoBlock,
  useGlobalMessageHandler,
  PersonLegislativeRolesBlock,
  PersonExecutiveRolesBlock,
  useUsPrefixLuts
} from 'components'
import { Box, CircularProgress, Stack, styled } from '@mui/material'
import {
  getUsPersonAddresses,
  getUsPersonUrls,
  type PersonBaseBasicInformation,
  type PersonUrl,
  usGetPersonBasicInfo,
  usGetLegislativeRoles,
  usGetExecutiveRoles,
  getExecCandidatesByPerson
} from 'services'
import { type LegislativeRoles } from 'services/person/us/edit/legislativeRolesService'
import { type ExecutivePosition, type ExecutiveStafferRole } from 'services/person/us/edit/executiveRolesService'
import { type ExecCandidate } from 'services/candidate/candidateService'
import { useParams } from 'react-router-dom'
import { useSessionContext } from 'session'
import UrlCard from 'components/common/person/edit/contact-info/url-card'
import { PersonAddressesPhonesCard } from 'components/common/person/edit/contact-info/address-phone-card'
import { getEmptyPersonBasicInfo } from 'helpers'

const CenteredCircularProgress = styled('div')({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)'
})

export const PersonUpdatePage: React.FC = () => {
  const { personId } = useParams<{ personId: string }>()
  const {
    changeModuleName,
    changeUniqueId
  } = useSessionContext()
  const { lutValues: usPrefixLutValues } = useUsPrefixLuts()
  const parsedPersonId = personId != null ? parseInt(personId, 10) : 1
  const [loading, setLoading] = useState(true)
  const { showErrorMessage } = useGlobalMessageHandler()
  const [personBasicInfo, setPersonBasicInfo] = useState<PersonBaseBasicInformation>(getEmptyPersonBasicInfo())
  const [roles, setRoles] = useState<LegislativeRoles | null>(null)
  const [executiveRoles, setExecutiveRoles] = useState<ExecutivePosition[] | null>(null)
  const [executiveStafferRoles, setExecutiveStafferRoles] = useState<ExecutiveStafferRole[] | null>(null)
  const [execCandidates, setExecCandidates] = useState<ExecCandidate[]>([])

  const [urls, setUrls] = useState<PersonUrl[]>([])
  const [addresses, setAddresses] = useState<PersonAddress[]>([])

  const updateBasicInfo = (newData: PersonBaseBasicInformation): void => {
    setPersonBasicInfo(newData)
  }

  useEffect(() => {
    changeModuleName('Person')
    const title = `${parsedPersonId} - ${usPrefixLutValues.prefixes.find(prefix => prefix.id === personBasicInfo.prefixId)?.code ?? ''} ${personBasicInfo.firstName ?? ''} ${personBasicInfo.middleName ?? ''} ${personBasicInfo.lastName ?? ''}`
    changeUniqueId(title)
  }, [parsedPersonId, personBasicInfo, usPrefixLutValues.prefixes])

  const refreshAddresses = async (updatedAddressId?: number): Promise<number | undefined> => {
    try {
      const addressesData = await getUsPersonAddresses(parsedPersonId)
      const sortedAddresses = addressesData.sort((a, b) => {
        if (a.typeId !== b.typeId) {
          return a.typeId - b.typeId
        }
        return (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
      })
      setAddresses(sortedAddresses)

      if (updatedAddressId !== undefined) {
        const newIndex = sortedAddresses.findIndex(addr => addr.id === updatedAddressId)
        return newIndex !== -1 ? newIndex : undefined
      }

      return undefined
    } catch (error) {
      console.error('Failed to refresh addresses:', error)
      showErrorMessage('Failed to refresh addresses')
      return undefined
    }
  }

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        setLoading(true)
        const personBasicInformationResponse = await usGetPersonBasicInfo(parsedPersonId)
        setPersonBasicInfo(personBasicInformationResponse)

        const rolesPromise = usGetLegislativeRoles(parsedPersonId)
          .then(data => { setRoles(data) })
          .catch(error => {
            console.error('Failed to fetch legislative roles:', error)
          })

        const executiveRolesPromise = usGetExecutiveRoles(parsedPersonId)
          .then(data => {
            setExecutiveRoles(data.positions)
            setExecutiveStafferRoles(data.stafferRoles)
          })
          .catch(() => {
            showErrorMessage('Failed to fetch executive roles')
            setExecutiveRoles([])
            setExecutiveStafferRoles([])
          })

        const execCandidatesPromise = getExecCandidatesByPerson(parsedPersonId)
          .then(data => { setExecCandidates(data) })
          .catch(error => {
            console.warn('Executive candidates endpoint not available:', error)
            setExecCandidates([])
          })

        const fetchRoles = async (): Promise<void> => {
          await Promise.all([rolesPromise, executiveRolesPromise, execCandidatesPromise])
        }

        const fetchUrls = async (): Promise<void> => {
          try {
            const urlsResponse = await getUsPersonUrls(parsedPersonId)
            if (urlsResponse.succeeded && urlsResponse.result !== null) {
              setUrls(urlsResponse.result)
            }
          } catch (error) {
            console.error('Failed to fetch URLs:', error)
            showErrorMessage('Failed to fetch URLs')
          }
        }

        const fetchAddresses = async (): Promise<void> => {
          try {
            const addressesData = await getUsPersonAddresses(parsedPersonId)
            setAddresses(addressesData.sort((a, b) => a.typeId - b.typeId))
          } catch (error) {
            console.error('Failed to fetch addresses:', error)
            showErrorMessage('Failed to fetch addresses')
          }
        }

        await fetchRoles()
        await fetchUrls()
        await fetchAddresses()
      } catch (error) {
        console.error('Failed to fetch data:', error)
        showErrorMessage('Failed to fetch data')
      } finally {
        setLoading(false)
      }
    }

    void fetchData()
  }, [parsedPersonId])

  return (
    <>
      {loading && <CenteredCircularProgress><CircularProgress size={100} /></CenteredCircularProgress>}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
        <Stack spacing={2} alignItems="center" mt={1} width="98%">
          <PersonBasicInfoBlock
            originalBasicInfo={personBasicInfo}
            loading={loading}
            updateBasicInfo={updateBasicInfo}
          />
          <PersonLegislativeRolesBlock roles={roles} personId={Number(personId)} />
          <PersonExecutiveRolesBlock executiveRoles={executiveRoles} stafferRoles={executiveStafferRoles} />
          <PersonAddressesPhonesCard
            personId={parsedPersonId}
            originalAddresses={addresses}
            loading={loading}
            onAddressesUpdate={setAddresses}
            refreshAddresses={refreshAddresses}
            roles={roles}
          />
          <UrlCard
            personId={parsedPersonId}
            urls={urls}
            loading={loading}
            setUrls={setUrls}
            roles={roles ?? undefined}
            execCandidates={execCandidates}
            executiveRoles={executiveRoles ?? undefined}
          />
          <PersonBackgroundInfoBlock personId={Number(personId)} startExpanded={true} />
        </Stack>
      </Box>
    </>
  )
}
